import { useMemo, useState } from "react";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import HeroStatusChip from "@/components/HeroStatusChip";
import AnalyticsInfraForecastSchoolsModal from "@/components/AnalyticsInfraForecastSchoolsModal";
import "../pages/AnalyticsInfrastructureTab.css";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const fmtLakhs = (v) => `₹${typeof v === "number" ? v.toFixed(1) : v}L`;

const computeInfraIndex = (summary) => {
  const total = summary?.total_classrooms || 0;
  if (!total) return 0;
  const repairShare = ((summary.current_repair_needed || 0) / total) * 100;
  const dilapidatedShare = ((summary.total_dilapidated || 0) / total) * 100;
  return Math.round((100 - repairShare - dilapidatedShare * 0.1) * 10) / 10;
};

const barColor = (rate) => {
  if (rate >= 10) return "ainf-red";
  if (rate >= 8) return "ainf-orange";
  return "";
};

const priorityTag = (rate, priority) => {
  if (rate >= 12 || priority === "High") return { label: rate >= 12 ? "Critical" : "High", className: "ainf-red" };
  if (rate >= 7 || priority === "Medium") return { label: "Medium", className: "" };
  return { label: "Watch", className: "ainf-blue" };
};

const suggestedAction = (rate, rank) => {
  if (rate >= 12) return "Safety audit";
  if (rate >= 9) return "Repair sprint";
  if (rate >= 7) return "Preventive repair";
  if (rate >= 6.5) return rank % 2 === 0 ? "Budget review" : "Cluster inspection";
  return "Monitor monthly";
};

const AnalyticsInfrastructureTab = ({
  infraData,
  loading = false,
  onRefresh,
  onGenerate,
  scopeLabel = "Pune District",
}) => {
  const [infraMetricModal, setInfraMetricModal] = useState(null);
  const summary = infraData?.summary;
  const blocks = useMemo(() => infraData?.block_forecast || [], [infraData?.block_forecast]);

  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => (b.repair_rate ?? 0) - (a.repair_rate ?? 0)),
    [blocks],
  );

  const top5 = useMemo(() => sortedBlocks.slice(0, 5), [sortedBlocks]);
  const top7 = useMemo(() => sortedBlocks.slice(0, 7), [sortedBlocks]);
  const maxRate = top5[0]?.repair_rate || 1;

  const infraIndex = useMemo(() => computeInfraIndex(summary), [summary]);

  const repairPct = useMemo(() => {
    const total = summary?.total_classrooms || 0;
    if (!total) return 0;
    return Math.round(((summary.current_repair_needed || 0) / total) * 1000) / 10;
  }, [summary]);

  const dilapidatedPct = useMemo(() => {
    const total = summary?.total_classrooms || 0;
    if (!total) return 0;
    return Math.round(((summary.total_dilapidated || 0) / total) * 1000) / 10;
  }, [summary]);

  const gaugeStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(var(--ainf-accent) 0 ${infraIndex}%, rgba(20,184,166,.14) ${infraIndex}% 100%)`,
    }),
    [infraIndex],
  );

  const budgetLabel = fmtLakhs(summary?.estimated_budget_lakhs ?? 0);

  const donutStyle = useMemo(
    () => ({
      background: `radial-gradient(circle at center, white 0 54%, transparent 55%), conic-gradient(var(--ainf-danger) 0 60%, var(--ainf-warning) 60% 90%, var(--ainf-accent) 90% 100%)`,
    }),
    [],
  );

  const topBlock = sortedBlocks[0];

  const defaultRoot = useMemo(
    () => [
      `${fmt(summary?.total_dilapidated)} dilapidated classrooms should be treated as safety-critical before general repairs are assigned.`,
      `Repair burden is ${repairPct}% across ${fmt(summary?.total_classrooms)} classrooms with ${fmt(summary?.current_repair_needed)} units needing immediate attention.`,
      topBlock
        ? `${topBlock.block} is the highest repair-share block at ${topBlock.repair_rate}% and should be reviewed first.`
        : "Identify the highest repair-share block for the first review cycle.",
    ],
    [summary, topBlock, repairPct],
  );

  const defaultRecs = useMemo(
    () => [
      `Allocate ${budgetLabel} with 60% for safety-critical repairs, 30% for preventive maintenance and 10% contingency.`,
      `Group ${fmt(summary?.forecast_repair_needed)} predicted repairs into block-level work orders before monsoon inspections.`,
      "Prioritize dilapidated classrooms and high-risk schools for immediate field verification.",
    ],
    [summary, budgetLabel],
  );

  const defaultActions = useMemo(
    () => [
      `Assign BEO safety audit for top ${Math.min(3, top7.length)} repair blocks within 7 days.`,
      "Publish block-wise repair evidence dashboard before monthly review.",
      "Track safety repairs weekly and preventive repairs monthly until repair burden drops below 2.5%.",
    ],
    [top7.length],
  );

  if (loading && !infraData) {
    return (
      <div className="analytics-infrastructure-view" data-testid="analytics-infrastructure-tab">
        <div className="ainf-loading">
          <div className="ainf-loading-spinner" />
          <span>Generating infrastructure forecast…</span>
        </div>
      </div>
    );
  }

  if (!infraData) {
    return (
      <div className="analytics-infrastructure-view" data-testid="analytics-infrastructure-tab">
        <div className="ainf-loading">
          <p style={{ margin: 0, fontSize: 18, color: "var(--ainf-text)", fontWeight: 900 }}>Infrastructure Forecast</p>
          <p style={{ margin: 0, maxWidth: 420, textAlign: "center" }}>
            AI-powered infrastructure gap analysis and budget forecast.
          </p>
          <button type="button" className="ainf-btn ainf-btn-primary" onClick={onGenerate}>
            Generate Forecast
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-infrastructure-view" data-testid="analytics-infrastructure-tab">
      <section className="ainf-page-header">
        <div>
          <div className="ainf-eyebrow">
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 21h18" />
              <path d="M5 21V7l8-4v18" />
              <path d="M19 21V11l-6-4" />
            </svg>
            AI-powered predictions · {scopeLabel} · 2025–26
          </div>
          <h1>Infrastructure Analytics</h1>
          <p>
            Monitor classroom capacity, repair burden, dilapidated risk and budget demand with a district-level action
            view that matches the executive dashboard layout.
          </p>
        </div>
        <div className="ainf-header-actions">
          <button type="button" className="ainf-btn">
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <path d="M7 10l5 5 5-5" />
              <path d="M12 15V3" />
            </svg>
            Export
          </button>
          <button type="button" className="ainf-btn ainf-btn-primary" onClick={onRefresh} disabled={loading}>
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <path d="M21 3v6h-6" />
            </svg>
            Refresh
          </button>
        </div>
      </section>

      <section className="ainf-hero-card">
        <div className="ainf-hero-layout">
          <div>
            <div className="ainf-health-main">
              <div className="ainf-gauge" style={gaugeStyle} aria-label={`Infrastructure overview ${infraIndex} percent`}>
                <div>
                  <strong>{infraIndex}</strong>
                  <span>Infra Overview</span>
                </div>
              </div>
              <div className="ainf-health-copy">
                <h2>Infrastructure Command Centre</h2>
                <p>
                  Overall infrastructure is {infraIndex >= 90 ? "strong" : "stable"}, but {fmt(summary?.total_dilapidated)}{" "}
                  dilapidated classrooms and {fmt(summary?.current_repair_needed)} current repairs need a focused
                  safety-first maintenance sprint.
                </p>
                <div className="ainf-status-row">
                  <HeroStatusChip chipClass="ainf-status-chip" showChevron={false}>
                    Strong Base
                  </HeroStatusChip>
                  {(summary?.total_dilapidated ?? 0) > 0 ? (
                    <HeroStatusChip
                      chipClass="ainf-status-chip"
                      variant="ainf-orange"
                      onClick={() => setInfraMetricModal("dilapidated")}
                      ariaLabel={`${fmt(summary?.total_dilapidated)} safety risk classrooms — view school list`}
                    >
                      {fmt(summary?.total_dilapidated)} Safety Risk
                    </HeroStatusChip>
                  ) : null}
                  {(summary?.current_repair_needed ?? 0) > 0 ? (
                    <HeroStatusChip
                      chipClass="ainf-status-chip"
                      variant="ainf-blue"
                      onClick={() => setInfraMetricModal("current_repair")}
                      ariaLabel={`${budgetLabel} budget need — view schools needing repairs`}
                    >
                      {budgetLabel} Budget Need
                    </HeroStatusChip>
                  ) : (
                    <HeroStatusChip chipClass="ainf-status-chip" variant="ainf-blue" showChevron={false}>
                      {budgetLabel} Budget Need
                    </HeroStatusChip>
                  )}
                </div>
              </div>
            </div>
            <div className="ainf-summary-note">
              <div className="ainf-icon-box ainf-green">
                <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2s8 4 8 10v4a6 6 0 0 1-12 0v-4c0-6 4-10 4-10Z" />
                  <path d="M8 13h8" />
                </svg>
              </div>
              <div>
                <b>Primary infrastructure action</b>
                <span>
                  Prioritize blocks with high dilapidated share first, then move to preventive repair forecasting for the
                  next school maintenance cycle.
                </span>
              </div>
            </div>
          </div>

          <div className="ainf-metric-grid" aria-label="Infrastructure KPIs">
            <article className="ainf-metric-card">
              <small>Total Classrooms</small>
              <strong>{fmt(summary?.total_classrooms)}</strong>
              <span>Physical capacity base</span>
            </article>
            <article
              className="ainf-metric-card ainf-warning clickable"
              role="button"
              tabIndex={0}
              aria-label="Current Repair: view schools needing classroom repairs"
              onClick={() => setInfraMetricModal("current_repair")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setInfraMetricModal("current_repair");
                }
              }}
            >
              <small>Current Repair</small>
              <strong>{fmt(summary?.current_repair_needed)}</strong>
              <span>{repairPct}% of classrooms</span>
            </article>
            <article className="ainf-metric-card ainf-accent">
              <small>Forecast Repair</small>
              <strong>{fmt(summary?.forecast_repair_needed)}</strong>
              <span>Predictive repair demand</span>
            </article>
            <article
              className="ainf-metric-card ainf-danger clickable"
              role="button"
              tabIndex={0}
              aria-label="Dilapidated: view schools with safety-critical classrooms"
              onClick={() => setInfraMetricModal("dilapidated")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setInfraMetricModal("dilapidated");
                }
              }}
            >
              <small>Dilapidated</small>
              <strong>{fmt(summary?.total_dilapidated)}</strong>
              <span>Safety-critical cases</span>
            </article>
          </div>
        </div>
      </section>

      <div className="ainf-section-title">
        <div>
          <h2>Infrastructure Snapshot</h2>
          <p>
            Four focused cards replace the older single-line KPI strip, creating clearer hierarchy while keeping all
            important numbers visible in the first fold.
          </p>
        </div>
        <button type="button" className="ainf-section-action">View diagnostics →</button>
      </div>

      <section className="ainf-domain-grid">
        <article className="ainf-domain-card">
          <div className="ainf-domain-head">
            <div className="ainf-icon-box ainf-green">
              <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
            </div>
            <span className="ainf-status-dot" />
          </div>
          <h3>Classroom Capacity</h3>
          <div className="ainf-domain-score">
            <strong>{fmt(summary?.total_classrooms)}</strong>
            <span>Total</span>
          </div>
          <div className="ainf-progress-line">
            <i style={{ width: `${Math.min(96, 100 - repairPct)}%`, background: "var(--ainf-accent)" }} />
          </div>
          <div className="ainf-domain-meta">
            <span>Operational base</span>
            <span>Strong</span>
          </div>
        </article>

        <article
          className="ainf-domain-card clickable"
          role="button"
          tabIndex={0}
          aria-label="Repair Backlog: view schools with pending repairs"
          onClick={() => setInfraMetricModal("repair_backlog")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setInfraMetricModal("repair_backlog");
            }
          }}
        >
          <div className="ainf-domain-head">
            <div className="ainf-icon-box ainf-orange">
              <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14.7 6.3a1 1 0 0 0-1.4 0l-8 8V18h3.7l8-8a1 1 0 0 0 0-1.4l-2.3-2.3Z" />
                <path d="M12 8l4 4" />
              </svg>
            </div>
            <span className="ainf-status-dot ainf-amber" />
          </div>
          <h3>Repair Backlog</h3>
          <div className="ainf-domain-score">
            <strong>{fmt(summary?.current_repair_needed)}</strong>
            <span>Current</span>
          </div>
          <div className="ainf-progress-line">
            <i style={{ width: `${Math.min(100, repairPct * 10)}%`, background: "var(--ainf-secondary)" }} />
          </div>
          <div className="ainf-domain-meta">
            <span>{repairPct}% classrooms</span>
            <span>{repairPct < 5 ? "Manageable" : "Elevated"}</span>
          </div>
        </article>

        <article
          className="ainf-domain-card clickable"
          role="button"
          tabIndex={0}
          aria-label="Dilapidated Risk: view schools with safety-critical classrooms"
          onClick={() => setInfraMetricModal("dilapidated")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setInfraMetricModal("dilapidated");
            }
          }}
        >
          <div className="ainf-domain-head">
            <div className="ainf-icon-box ainf-red">
              <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
              </svg>
            </div>
            <span className="ainf-status-dot ainf-red" />
          </div>
          <h3>Dilapidated Risk</h3>
          <div className="ainf-domain-score">
            <strong>{fmt(summary?.total_dilapidated)}</strong>
            <span>Critical</span>
          </div>
          <div className="ainf-progress-line">
            <i style={{ width: `${Math.min(100, dilapidatedPct * 10)}%`, background: "var(--ainf-danger)" }} />
          </div>
          <div className="ainf-domain-meta">
            <span>Safety-first</span>
            <span>High priority</span>
          </div>
        </article>

        <article className="ainf-domain-card">
          <div className="ainf-domain-head">
            <div className="ainf-icon-box">
              <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1v22" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <span className="ainf-status-dot" />
          </div>
          <h3>Estimated Budget</h3>
          <div className="ainf-domain-score">
            <strong>{budgetLabel}</strong>
            <span>Total</span>
          </div>
          <div className="ainf-progress-line">
            <i style={{ width: "71%" }} />
          </div>
          <div className="ainf-domain-meta">
            <span>Need-based</span>
            <span>Data-driven</span>
          </div>
        </article>
      </section>

      <div className="ainf-section-title">
        <div>
          <h2>Repair Burden and Budget Intelligence</h2>
          <p>Charts now follow the same spacious panel style as the executive dashboard and separate diagnosis from action.</p>
        </div>
        <button type="button" className="ainf-section-action">Go to block list →</button>
      </div>

      <section className="ainf-analytics-grid">
        <article className="ainf-panel">
          <div className="ainf-panel-head">
            <div>
              <h3>Top Blocks by Repair Percentage</h3>
              <p>Repair burden highlights where preventive maintenance should start first.</p>
            </div>
            <span className="ainf-pill ainf-red">High risk</span>
          </div>
          <div className="ainf-bar-list" aria-label="Repair burden by block">
            {top5.map((block) => {
              const widthPct = Math.round(((block.repair_rate || 0) / maxRate) * 100);
              const color = barColor(block.repair_rate);
              return (
                <div className="ainf-hbar-row" key={block.block}>
                  <div className="ainf-hbar-label">{block.block}</div>
                  <div className="ainf-hbar-track">
                    <div className={`ainf-hbar ${color}`} style={{ width: `${widthPct}%` }} />
                  </div>
                  <div className="ainf-hbar-value">{block.repair_rate}%</div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="ainf-panel ainf-budget-card">
          <div className="ainf-panel-head">
            <div>
              <h3>Budget Allocation Logic</h3>
              <p>Recommended split converts AI insights into a clear spending model.</p>
            </div>
            <span className="ainf-pill ainf-green">{budgetLabel}</span>
          </div>
          <div className="ainf-budget-hero">
            <div className="ainf-donut" style={donutStyle} aria-label="Budget allocation donut">
              <b>{budgetLabel}</b>
            </div>
            <div className="ainf-legend">
              <div className="ainf-legend-row">
                <span><i className="ainf-dot" />Safety-critical repairs</span>
                <b>60%</b>
              </div>
              <div className="ainf-legend-row">
                <span><i className="ainf-dot" />Preventive repairs</span>
                <b>30%</b>
              </div>
              <div className="ainf-legend-row">
                <span><i className="ainf-dot" />Contingency</span>
                <b>10%</b>
              </div>
            </div>
          </div>
          <div className="ainf-budget-note">
            <b>AI recommendation:</b> Fund dilapidated classrooms first, then distribute preventive repair budget to high
            repair-share blocks before monsoon and annual school inspection cycles.
          </div>
        </article>
      </section>

      <div className="ainf-section-title">
        <div>
          <h2>Block Infrastructure Status</h2>
          <p>The table is retained, but with better spacing, clear priority tags, and an action column for review flow.</p>
        </div>
        <button type="button" className="ainf-section-action">View action queue →</button>
      </div>

      <section className="ainf-two-column">
        <article className="ainf-panel ainf-table-card">
          <div className="ainf-table-head">
            <div>
              <h3>Priority Repair Ranking</h3>
              <p>Blocks sorted by repair percentage, budget demand and safety risk.</p>
            </div>
            <span className="ainf-pill">Top {top7.length}</span>
          </div>
          <table className="ainf-table" aria-label="Block infrastructure repair ranking">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>Repair %</th>
                <th>Budget</th>
                <th>Priority</th>
                <th>Suggested action</th>
              </tr>
            </thead>
            <tbody>
              {top7.map((block, index) => {
                const tag = priorityTag(block.repair_rate, block.priority);
                return (
                  <tr key={block.block}>
                    <td>{index + 1}</td>
                    <td>{block.block}</td>
                    <td className="ainf-score">{block.repair_rate}%</td>
                    <td>{fmtLakhs(block.estimated_budget_lakhs)}</td>
                    <td>
                      <span className={`ainf-tag ${tag.className}`}>{tag.label}</span>
                    </td>
                    <td>{suggestedAction(block.repair_rate, index)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </article>

        <InterventionActionPlan
          aiContent={infraData?.ai_insights}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="ainf-section-title">
        <div>
          <h2>Execution Workflow</h2>
          <p>Simple operational flow for converting dashboard intelligence into district-level repair action.</p>
        </div>
        <button type="button" className="ainf-section-action" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          Back to top ↑
        </button>
      </div>

      <section className="ainf-workflow-grid">
        <article className="ainf-step-card" data-step="01">
          <div className="ainf-icon-box ainf-red">
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.3 3.8 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" />
            </svg>
          </div>
          <h3>Flag Safety Risk</h3>
          <p>Identify dilapidated classrooms and high-risk schools for immediate field verification.</p>
        </article>
        <article className="ainf-step-card" data-step="02">
          <div className="ainf-icon-box ainf-orange">
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l-6 3V6l6-3 6 3 6-3v15l-6 3-6-3Z" />
            </svg>
          </div>
          <h3>Cluster Work Orders</h3>
          <p>Group repairs by block and nearby school clusters to optimize inspection and contractor visits.</p>
        </article>
        <article className="ainf-step-card" data-step="03">
          <div className="ainf-icon-box">
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1v22" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7H14a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <h3>Allocate Budget</h3>
          <p>Split the {budgetLabel} budget into safety, preventive and contingency buckets.</p>
        </article>
        <article className="ainf-step-card" data-step="04">
          <div className="ainf-icon-box ainf-green">
            <svg className="ainf-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 4h14v16H5z" />
              <path d="M9 2h6v4H9z" />
              <path d="M9 11l2 2 4-4" />
            </svg>
          </div>
          <h3>Track Closure</h3>
          <p>Monitor completion, evidence upload and reinspection status from the same dashboard.</p>
        </article>
      </section>

      <AnalyticsInfraForecastSchoolsModal
        open={Boolean(infraMetricModal)}
        metric={infraMetricModal}
        onClose={() => setInfraMetricModal(null)}
        scopeLabel={scopeLabel}
        districtPct={infraMetricModal === "dilapidated" ? dilapidatedPct : repairPct}
      />
    </div>
  );
};

export default AnalyticsInfrastructureTab;
